import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const CONVERTER_TYPES = ["dayend_sameday", "dayend_nextday"];

export default function ConverterForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState([]);
  const [converters, setConverters] = useState([]);
  const [form, setForm] = useState({
    store_id: "",
    converter_type: "dayend_sameday",
    active: true,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [storeResponse, converterResponse] = await Promise.all([
          api.get("/stores"),
          api.get("/converters"),
        ]);
        if (cancelled) return;

        const converterList = converterResponse.data || [];
        setStores(storeResponse.data || []);
        setConverters(converterList);

        if (isEdit) {
          let converter = converterList.find((item) => item.id === id);
          if (!converter) {
            const detailResponse = await api.get(`/converters/${id}`);
            converter = detailResponse.data;
          }
          if (!cancelled && converter) {
            setForm({
              store_id: converter.store_id || "",
              converter_type: CONVERTER_TYPES.includes(converter.converter_type)
                ? converter.converter_type
                : CONVERTER_TYPES.includes(converter.file_type)
                  ? converter.file_type
                  : "dayend_sameday",
              active: converter.active !== false,
            });
          }
        }
      } catch (error) {
        toast.error(formatApiError(error.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const availableStores = useMemo(() => {
    const assigned = new Set(
      converters
        .filter((converter) => !isEdit || converter.id !== id)
        .map((converter) => converter.store_id)
    );
    return [...stores]
      .filter((store) => !assigned.has(store.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [converters, id, isEdit, stores]);

  const update = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/converters/${id}`, form);
        toast.success(t("converterForm.updated"));
      } else {
        await api.post("/converters", form);
        toast.success(t("converterForm.created"));
      }
      navigate("/converters");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="pg-card p-10 text-center text-slate-400">{t("common.loading")}</div>;
  }

  return (
    <div data-testid="converter-form-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-2 mb-4"
      >
        <ArrowLeft size={15} /> {t("common.back")}
      </button>

      <div className="mb-7">
        <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
          {t("converterForm.breadcrumb", {
            mode: isEdit ? t("converterForm.modeEdit") : t("converterForm.modeCreate"),
          })}
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900">
          {isEdit ? t("converterForm.editTitle") : t("converterForm.addTitle")}
        </h1>
      </div>

      <form onSubmit={submit} className="space-y-6 max-w-3xl">
        <section className="pg-card p-6 lg:p-8">
          <div className="field-section-title mb-5">
            <RefreshCw size={16} /> {t("converterForm.details")}
          </div>
          <p className="text-sm text-slate-500 mb-6">{t("converterForm.description")}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t("converterForm.store")}>
              <select
                className={inputClass}
                required
                value={form.store_id}
                onChange={update("store_id")}
                data-testid="converter-store"
              >
                <option value="">{t("converterForm.selectStore")}</option>
                {availableStores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name} · MID {store.machine_id}
                  </option>
                ))}
              </select>
              {availableStores.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">{t("converterForm.noStores")}</p>
              )}
            </Field>

            <Field label={t("converterForm.type")}>
              <select
                className={inputClass}
                required
                value={form.converter_type}
                onChange={update("converter_type")}
                data-testid="converter-type"
              >
                <option value="dayend_sameday">{t("converters.types.sameday")}</option>
                <option value="dayend_nextday">{t("converters.types.nextday")}</option>
              </select>
            </Field>
          </div>

          <label className="mt-6 inline-flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.active}
              onChange={update("active")}
              className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              data-testid="converter-active"
            />
            <span className="text-sm font-medium text-slate-700">{t("converterForm.active")}</span>
          </label>
        </section>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-sm"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={saving || !form.store_id}
            className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60"
            data-testid="save-converter-btn"
          >
            <Save size={15} />
            {saving
              ? t("converterForm.saving")
              : isEdit
                ? t("converterForm.update")
                : t("converterForm.create")}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputClass =
  "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none transition bg-white text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}
